<template>
  <app-layout>
    <div class="sc-container pd-page">

      <div class="detail-layout">
        <!-- MAIN COLUMN -->
        <div class="detail-main">

          <!-- Title bar -->
          <div class="sc-card">
            <div class="sc-card-body title-bar">
              <div class="title-bar-left">
                <h1 class="detail-title">{{ details.name || details.title }}</h1>
                <div class="detail-meta">
                  <span class="meta-date">{{ formatDate(details.created_at) }}</span>
                  <template v-if="details.interests && details.interests.length">
                    <a
                      v-for="int in details.interests"
                      :key="int.id"
                      :href="'/plan/category/' + int.interest.slug"
                      class="meta-tag"
                    >{{ int.interest.name }}</a>
                  </template>
                </div>
              </div>
              <div class="title-bar-right">
                <button
                  v-if="details.user && (details.user.id == id || details.user.owner_id == id)"
                  class="btn-outline"
                  @click="openUpdateProject()"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit
                </button>
                <button
                  v-if="details.user && (details.user.id == id || details.user.owner_id == id)"
                  class="btn-danger"
                  @click="confirmDelete = true"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                  Delete
                </button>
                <button
                  v-if="id && details.user && details.user.id != id && details.user.owner_id != id"
                  class="btn-primary"
                  @click="pdQuoteOpen = true"
                >
                  Request a Quote
                </button>
              </div>
            </div>
          </div>

          <!-- Gallery -->
          <div v-if="details.images && details.images.length > 0">
            <div class="gallery-hero" @click="showImg(activeImageIndex)">
              <img :src="details.images[activeImageIndex].path" :alt="details.name || details.title" />
              <div class="gallery-overlay-hint">Click to expand</div>
            </div>
            <div class="gallery-thumbs">
              <div
                v-for="(img, i) in details.images"
                :key="img.id || i"
                class="gallery-thumb"
                :class="{ active: i === activeImageIndex }"
                @click="activeImageIndex = i"
              >
                <img :src="img.path" :alt="details.name || details.title" />
              </div>
            </div>
          </div>
          <div v-else class="gallery-placeholder">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            <span>No images available</span>
          </div>

          <vue-easy-lightbox
            :visible="visible"
            :imgs="details.images ? details.images.map(i => i.path) : []"
            :index="activeImageIndex"
            @hide="handleHide"
          />

          <!-- Tabs + actions row -->
          <div class="sc-card pd-tabs-bar">
            <div class="filter-tabs">
              <button class="filter-tab" :class="{ active: activeTab === 'overview' }" @click="activeTab = 'overview'">Overview</button>
              <button class="filter-tab" :class="{ active: activeTab === 'comments' }" @click="activeTab = 'comments'">
                Comments
                <span v-if="details.comments && details.comments.length" class="pd-tab-count">{{ details.comments.length }}</span>
              </button>
            </div>
            <div class="pd-actions-bar">
              <div class="pd-likes-wrap">
                <button
                  class="pd-like-btn"
                  :class="{ 'pd-like-btn--liked': alreadyLiked }"
                  :disabled="likeLoading || alreadyLiked"
                  @click="likePlan"
                  :title="alreadyLiked ? 'You liked this' : 'Like this concept'"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" :fill="alreadyLiked ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  <span>{{ details.likes ? details.likes.length : 0 }}</span>
                </button>
                <button v-if="details.likes && details.likes.length" class="pd-likers-btn" @click="showLikersModal = true" title="See who liked this">
                  <span class="pd-likers-stack">
                    <img v-for="(l, i) in details.likes.slice(0, 4)" :key="i"
                      :src="l.user && l.user.avatar ? $avatarSrc(l.user.avatar, 'users') : initAvatar(l.user ? l.user.name : '')"
                      class="pd-likers-avatar" :style="{ zIndex: 4 - i }" />
                  </span>
                  <span v-if="details.likes.length > 4" class="pd-likers-more">+{{ details.likes.length - 4 }}</span>
                </button>
              </div>
            </div>

            <transition name="app-drop">
              <div v-if="showLikersModal" class="likers-overlay" @click.self="showLikersModal = false">
                <div class="likers-modal">
                  <div class="likers-modal__header">
                    <span>{{ details.likes.length }} {{ details.likes.length === 1 ? 'Like' : 'Likes' }}</span>
                    <button class="likers-modal__close" @click="showLikersModal = false">
                      <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                  <div class="likers-modal__body">
                    <router-link v-for="l in details.likes" :key="l.id"
                      :to="l.user ? '/sc/profile/' + l.user.slug : '#'"
                      class="likers-item" @click="showLikersModal = false">
                      <img :src="l.user && l.user.avatar ? $avatarSrc(l.user.avatar, 'users') : initAvatar(l.user ? l.user.name : '?')"
                        class="likers-item__avatar" @error="e => e.target.src = initAvatar(l.user ? l.user.name : '?')" />
                      <div class="likers-item__info">
                        <span class="likers-item__name">{{ l.user ? l.user.name : 'Unknown' }}</span>
                        <span v-if="l.user && l.user.profession" class="likers-item__role">{{ l.user.profession }}</span>
                      </div>
                    </router-link>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <!-- Overview tab -->
          <div v-if="activeTab === 'overview'">
            <div class="sc-card">
              <div class="sc-card-header">Description</div>
              <div class="sc-card-body prose-content" v-html="details.description"></div>
            </div>

            <!-- Video -->
            <div v-if="youtubeVideoId" class="sc-card">
              <div class="sc-card-header">Video</div>
              <div class="sc-card-body">
                <div class="sc-video-wrap">
                  <iframe
                    :src="'https://www.youtube.com/embed/' + youtubeVideoId"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                    class="sc-video-frame"
                  ></iframe>
                </div>
              </div>
            </div>

            <!-- Instagram -->
            <div v-if="instagramShortcode" class="sc-card">
              <div class="sc-card-header">Instagram</div>
              <div class="sc-card-body">
                <div class="sc-video-wrap sc-video-wrap--instagram">
                  <iframe
                    :src="'https://www.instagram.com/p/' + instagramShortcode + '/embed'"
                    frameborder="0"
                    scrolling="no"
                    allowtransparency="true"
                    class="sc-video-frame"
                  ></iframe>
                </div>
              </div>
            </div>

            <!-- Plan Details -->
            <div class="sc-card">
              <div class="sc-card-header">Plan Details</div>
              <div class="sc-card-body">
                <div class="details-grid">
                  <div class="detail-row">
                    <span class="detail-label">Height</span>
                    <span class="detail-value">{{ details.height || '--' }}</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Land Size</span>
                    <span class="detail-value">
                      <template v-if="details.land_size">
                        {{ details.land_size }}
                        <span v-if="details.land_size_unit === 'M'">m&#178;</span>
                        <span v-else-if="details.land_size_unit === 'KM'">km&#178;</span>
                        <span v-else>{{ details.land_size_unit }}</span>
                      </template>
                      <template v-else>--</template>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Comments tab -->
          <div v-if="activeTab === 'comments'" class="sc-card">
            <div class="sc-card-header">Comments</div>
            <div v-if="details.comments && details.comments.length">
              <div
                v-for="com in details.comments"
                :key="com.id"
                class="pd-comment"
              >
                <div class="pd-comment-avatar">
                  <router-link :to="'/sc/profile/' + com.user.slug">
                    <img
                      v-if="com.user && com.user.avatar"
                      :src="image_url + 'users/' + com.user.avatar"
                      class="pd-avatar-img"
                      alt=""
                    />
                    <img v-else src="/assets/avatar.png" class="pd-avatar-img" alt="" />
                  </router-link>
                </div>
                <div class="pd-comment-body">
                  <div class="pd-comment-header">
                    <router-link class="pd-comment-name" :to="'/sc/profile/' + com.user.slug">{{ com.user.name }}</router-link>
                    <span class="pd-comment-date">{{ formatDate(com.created_at) }}</span>
                    <div v-if="com.user.id == id" class="pd-comment-menu">
                      <button class="pd-menu-btn" @click="toggleMenu(com.id)">&#8943;</button>
                      <div v-if="openMenuId === com.id" class="pd-dropdown">
                        <button @click="editPostComment(com.id, com.comment); openMenuId = null">Edit</button>
                        <button @click="deleteComment(com.id); openMenuId = null">Delete</button>
                      </div>
                    </div>
                  </div>
                  <div class="pd-comment-text">{{ com.comment }}</div>
                  <div class="pd-comment-actions">
                    <span class="pd-action-count">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                      {{ com.likes.length }}
                    </span>
                    <button v-if="checkIfUser(com.likes)" class="pd-action-btn" @click="unlikePostComment(com.id)">Unlike</button>
                    <button v-else class="pd-action-btn" @click="likePostComment(com.id)">Like</button>
                    <button class="pd-action-btn" @click="replyPostCommentModal(com.id)">Reply</button>
                  </div>

                  <!-- Level 2 replies -->
                  <div v-if="com.sub && com.sub.length" class="pd-replies">
                    <div v-for="sub in com.sub" :key="sub.id" class="pd-comment pd-comment--reply">
                      <div class="pd-comment-avatar">
                        <router-link :to="'/sc/profile/' + sub.user.slug">
                          <img
                            v-if="sub.user && sub.user.avatar"
                            :src="image_url + 'users/' + sub.user.avatar"
                            class="pd-avatar-img pd-avatar-img--sm"
                            alt=""
                          />
                          <img v-else src="/assets/avatar.png" class="pd-avatar-img pd-avatar-img--sm" alt="" />
                        </router-link>
                      </div>
                      <div class="pd-comment-body">
                        <div class="pd-comment-header">
                          <router-link class="pd-comment-name" :to="'/sc/profile/' + sub.user.slug">{{ sub.user.name }}</router-link>
                          <span class="pd-comment-date">{{ formatDate(sub.created_at) }}</span>
                          <div v-if="sub.user.id == id" class="pd-comment-menu">
                            <button class="pd-menu-btn" @click="toggleMenu(sub.id)">&#8943;</button>
                            <div v-if="openMenuId === sub.id" class="pd-dropdown">
                              <button @click="editPostComment(sub.id, sub.comment); openMenuId = null">Edit</button>
                              <button @click="deleteComment(sub.id); openMenuId = null">Delete</button>
                            </div>
                          </div>
                        </div>
                        <div class="pd-comment-text">{{ sub.comment }}</div>
                        <div class="pd-comment-actions">
                          <span class="pd-action-count">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                            {{ sub.likes.length }}
                          </span>
                          <button v-if="checkIfUser(sub.likes)" class="pd-action-btn" @click="unlikePostComment(sub.id)">Unlike</button>
                          <button v-else class="pd-action-btn" @click="likePostComment(sub.id)">Like</button>
                          <button class="pd-action-btn" @click="replyPostCommentModal(sub.id)">Reply</button>
                        </div>

                        <!-- Level 3 replies -->
                        <div v-if="sub.sub && sub.sub.length" class="pd-replies">
                          <div v-for="sb in sub.sub" :key="sb.id" class="pd-comment pd-comment--reply">
                            <div class="pd-comment-avatar">
                              <router-link :to="'/sc/profile/' + sb.user.slug">
                                <img
                                  v-if="sb.user && sb.user.avatar"
                                  :src="image_url + 'users/' + sb.user.avatar"
                                  class="pd-avatar-img pd-avatar-img--sm"
                                  alt=""
                                />
                                <img v-else src="/assets/avatar.png" class="pd-avatar-img pd-avatar-img--sm" alt="" />
                              </router-link>
                            </div>
                            <div class="pd-comment-body">
                              <div class="pd-comment-header">
                                <router-link class="pd-comment-name" :to="'/sc/profile/' + sb.user.slug">{{ sb.user.name }}</router-link>
                                <span class="pd-comment-date">{{ formatDate(sb.created_at) }}</span>
                                <div v-if="sb.user.id == id" class="pd-comment-menu">
                                  <button class="pd-menu-btn" @click="toggleMenu(sb.id)">&#8943;</button>
                                  <div v-if="openMenuId === sb.id" class="pd-dropdown">
                                    <button @click="editPostComment(sb.id, sb.comment); openMenuId = null">Edit</button>
                                    <button @click="deleteComment(sb.id); openMenuId = null">Delete</button>
                                  </div>
                                </div>
                              </div>
                              <div class="pd-comment-text">{{ sb.comment }}</div>
                              <div class="pd-comment-actions">
                                <span class="pd-action-count">
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                                  {{ sb.likes.length }}
                                </span>
                                <button v-if="checkIfUser(sb.likes)" class="pd-action-btn" @click="unlikePostComment(sb.id)">Unlike</button>
                                <button v-else class="pd-action-btn" @click="likePostComment(sb.id)">Like</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="pd-no-comments">No comments yet. Be the first!</div>

            <!-- Add comment -->
            <div class="sc-card-body pd-comment-input-wrap">
              <textarea
                class="pd-comment-input"
                placeholder="Add a comment…"
                rows="3"
                :id="'submitCommentButton' + details.id"
                v-model="comment.description[index]"
                @keydown.enter.exact.prevent
                @keyup.enter.exact="submitComment($event, details.id)"
                @keydown.enter.shift.exact="newline"
              ></textarea>
              <button
                class="pd-comment-send"
                :disabled="loading"
                @click="sendSubmitComment(details.id)"
              >
                <svg v-if="!loading" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                <span v-else class="pd-spinner"></span>
              </button>
            </div>
          </div>

          <!-- Related Plans -->
          <div v-if="related_plans.length > 0">
            <div class="sc-card">
              <div class="sc-card-header">Related Plans</div>
            </div>
            <div class="pd-related-grid">
              <div v-for="(data, index) in related_plans" :key="index">
                <div v-if="data.plan" class="sc-card pd-related-card">
                  <router-link :to="'/plan/' + data.plan.slug">
                    <img
                      v-if="data.plan.images && data.plan.images.length > 0"
                      :src="data.plan.images[0].path"
                      class="pd-related-img"
                      alt=""
                    />
                    <div v-else class="pd-related-img-placeholder">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    </div>
                  </router-link>
                  <div class="pd-related-body">
                    <span v-if="data.interest" class="status-badge badge-success">{{ data.interest.name }}</span>
                    <router-link :to="'/plan/' + data.plan.slug" class="pd-related-title">{{ data.plan.name || data.plan.title }}</router-link>
                    <div v-if="data.plan.user" class="pd-related-author">
                      <router-link
                        :to="data.plan.user.type === 'company' ? '/company/' + data.plan.user.slug : '/sc/profile/' + data.plan.user.slug"
                        target="_blank"
                      >
                        <img
                          v-if="data.plan.user.avatar"
                          :src="image_url + 'users/' + data.plan.user.avatar"
                          class="pd-avatar-img pd-avatar-img--sm"
                          alt=""
                        />
                        <img v-else :src="image_url + 'avatar.png'" class="pd-avatar-img pd-avatar-img--sm" alt="" />
                        <span>{{ data.plan.user.name }}</span>
                      </router-link>
                      <span class="pd-related-date">{{ formatDate(data.plan.created_at) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="infinite-wrapper">
              <infinite-loading force-use-infinite-wrapper=".infinite-wrapper" @distance="3" @infinite="handleLoadMore"></infinite-loading>
            </div>
          </div>

        </div>

        <!-- SIDEBAR -->
        <aside class="detail-sidebar">

          <!-- Author card -->
          <div v-if="details.user" class="sc-card pd-author-card">
            <div class="sc-card-header">Posted by</div>
            <div class="sc-card-body pd-author-body">
              <a
                :href="details.user.type === 'company' ? '/company/' + details.user.company.slug : '/sc/profile/' + details.user.slug"
                target="_blank"
                class="pd-author-link"
              >
                <img
                  v-if="details.user.avatar"
                  :src="image_url + 'users/' + details.user.avatar"
                  class="pd-author-avatar"
                  alt=""
                />
                <img v-else src="/assets/avatar.png" class="pd-author-avatar" alt="" />
                <div class="pd-author-info">
                  <strong class="pd-author-name">{{ details.user.name }}</strong>
                  <template v-if="details.user.type === 'company' && details.user.currentcompany">
                    <span class="pd-author-sub">
                      {{ details.user.current_position }}
                      <router-link :to="'/company/' + details.user.currentcompany.slug">{{ details.user.currentcompany.name }}</router-link>
                    </span>
                  </template>
                  <template v-else>
                    <span class="pd-author-sub">{{ details.user.profession }}</span>
                  </template>
                  <span class="pd-author-loc" v-if="details.user.state || details.user.country">
                    {{ details.user.state ? details.user.state.name + ', ' : '' }}{{ details.user.country ? details.user.country.name : '' }}
                  </span>
                </div>
              </a>
            </div>
          </div>

          <!-- Request quote CTA -->
          <div class="sc-card pd-quote-card">
            <div class="sc-card-body pd-quote-body">
              <p>Want a similar design for your project?</p>
              <button class="btn-primary pd-quote-btn" @click="pdQuoteOpen = true">Request a Quote</button>
            </div>
          </div>

          <!-- Adverts -->
          <div v-if="adverts.length" class="sc-card pd-advert-card">
            <div class="pd-advert-inner">
              <template v-for="(data, i) in adverts" :key="data.id">
                <a v-if="i === pdAdvertIndex" :href="data.link" target="_blank">
                  <img :src="image_url + 'advert/' + data.image" class="pd-advert-img" alt="" />
                </a>
              </template>
              <div v-if="adverts.length > 1" class="pd-advert-dots">
                <button
                  v-for="(_, i) in adverts"
                  :key="i"
                  class="pd-advert-dot"
                  :class="{ active: i === pdAdvertIndex }"
                  @click="pdAdvertIndex = i"
                ></button>
              </div>
            </div>
          </div>

        </aside>
      </div>
    </div>

    <!-- Edit Plan Modal -->
    <transition name="pd-fade">
      <div v-if="showEditModal" class="pd-modal-backdrop" @click.self="cancelForm()">
        <div class="pd-modal">
          <div class="pd-modal-header">
            <h3 class="pd-modal-title">Update Plan</h3>
            <button ref="pdEditClose" class="pd-modal-close" @click="cancelForm()">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="pd-modal-body">

            <!-- Title -->
            <div class="pd-field">
              <label class="pd-label">Title <span class="req">*</span></label>
              <input type="text" class="pd-input" v-model="plan.title" required />
            </div>

            <!-- Category -->
            <div class="pd-field">
              <label class="pd-label">Category <span class="req">*</span></label>
              <div class="pd-category-row">
                <select class="pd-input" v-model="plan.project_interest_1" @change="fectchInterestChildren($event)">
                  <option value="" disabled>Select category</option>
                  <option v-for="d in interests" :key="d.id" :value="d.id">{{ d.name }}</option>
                </select>
                <select v-if="interest_children.length" class="pd-input" v-model="plan.project_interest_2" @change="fectchInterestChildren2($event)">
                  <option value="" disabled>Sub-category</option>
                  <option v-for="d in interest_children" :key="d.id" :value="d.id">{{ d.name }}</option>
                </select>
                <select v-if="interest_children2.length" class="pd-input" v-model="plan.project_interest_3">
                  <option value="" disabled>Sub-category 2</option>
                  <option v-for="d in interest_children2" :key="d.id" :value="d.id">{{ d.name }}</option>
                </select>
              </div>
            </div>

            <!-- Description -->
            <div class="pd-field">
              <label class="pd-label">Description <span class="req">*</span></label>
              <QuillEditor v-model:content="plan.description" contentType="html" :toolbar="customToolbar" />
            </div>

            <!-- Height -->
            <div class="pd-field">
              <label class="pd-label">Height</label>
              <select class="pd-input" v-model="plan.height">
                <option value="" disabled>Select height</option>
                <option value="Low rise (Less than 3 Storeys)">Low rise (Less than 3 Storeys)</option>
                <option value="Mid rise (4 - 10 Storeys)">Mid rise (4 - 10 Storeys)</option>
                <option value="High rise (11 - 30 Storeys)">High rise (11 - 30 Storeys)</option>
                <option value="Skyscrappers (above 30 storeys)">Skyscrappers (above 30 storeys)</option>
              </select>
            </div>

            <!-- Land Size -->
            <div class="pd-field">
              <label class="pd-label">Land Size <span class="req">*</span></label>
              <div class="pd-row-2">
                <input type="text" class="pd-input" v-model="plan.land_size" placeholder="Size" />
                <select class="pd-input" v-model="plan.land_size_unit">
                  <option value="" disabled>Unit</option>
                  <option value="M">M</option>
                  <option value="Plot">Plot</option>
                  <option value="Acre">Acre</option>
                  <option value="Hectares">Hectares</option>
                  <option value="KM">KM</option>
                </select>
              </div>
            </div>

            <!-- YouTube -->
            <div class="pd-field">
              <label class="pd-label">YouTube URL</label>
              <input type="url" class="pd-input" v-model="plan.youtube_url" placeholder="https://www.youtube.com/watch?v=…" />
            </div>

            <!-- Instagram -->
            <div class="pd-field">
              <label class="pd-label">Instagram URL</label>
              <input type="url" class="pd-input" v-model="plan.instagram_url" placeholder="https://www.instagram.com/p/…" />
            </div>

            <!-- Existing images -->
            <div v-if="details.images && details.images.length" class="pd-field">
              <label class="pd-label">Existing Images</label>
              <div class="pd-img-grid">
                <div v-for="(data, index) in details.images" :key="index" class="pd-img-thumb" :class="{ 'pd-img-thumb--cover': data.is_cover }">
                  <img :src="data.path" alt="" />
                  <button type="button" class="pd-img-remove" @click="deleteProjectImage(data.id)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                  <button
                    type="button"
                    class="pd-img-cover"
                    :class="{ 'pd-img-cover--active': data.is_cover }"
                    :title="data.is_cover ? 'Cover image' : 'Set as cover'"
                    @click="setCoverImage(data.id)"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" :fill="data.is_cover ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Upload new images -->
            <div class="pd-field">
              <label class="pd-label">Upload Images</label>
              <div id="my-strictly-unique-vue-upload-multiple-image" style="display: flex; justify-content: center;">
                <vue-upload-multiple-image
                  @upload-success="uploadImageSuccess"
                  @before-remove="beforeRemove"
                  @edit-image="editImage"
                  @data-change="dataChange"
                  :data-images="images"
                  dragText="Drag and drop images"
                  browseText="Browse Images"
                  primaryText="added"
                  markIsPrimaryText="Set default image"
                  popupText="Description default image"
                  :maxImage="5"
                ></vue-upload-multiple-image>
              </div>
            </div>

          </div>
          <div class="pd-modal-footer">
            <button class="btn-outline" @click="cancelForm()">Cancel</button>
            <button class="btn-primary" :disabled="loading" @click="updateProject()">
              <span v-if="loading" class="pd-spinner"></span>
              <span v-else>Update Plan</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Request a Quote Modal -->
    <transition name="pd-fade">
      <div v-if="pdQuoteOpen" class="pd-modal-backdrop" @click.self="pdQuoteOpen = false">
        <div class="pd-modal">
          <div class="pd-modal-header">
            <h3 class="pd-modal-title">Request a Quote</h3>
            <button class="pd-modal-close" @click="pdQuoteOpen = false">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="pd-modal-body" v-if="id">
            <form id="message-form">
              <div class="pd-field">
                <label class="pd-label">Brief</label>
                <textarea class="pd-input pd-textarea" rows="5" name="brief" placeholder="Your message here…" required></textarea>
              </div>

              <div class="pd-field">
                <label class="pd-label">Land Size <span class="req">*</span></label>
                <div class="pd-row-2">
                  <input type="text" class="pd-input" name="land_size" oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');" required />
                  <select class="pd-input" name="land_size_unit" required>
                    <option value="" disabled selected>Unit</option>
                    <option value="M">M&#178;</option>
                    <option value="Plot">Plot</option>
                    <option value="Acre">Acre</option>
                    <option value="Hectares">Hectares</option>
                    <option value="KM">KM&#178;</option>
                  </select>
                </div>
              </div>

              <div class="pd-field">
                <label class="pd-label">Proposed Property Address</label>
                <textarea class="pd-input pd-textarea" rows="3" name="property_address" required></textarea>
              </div>

              <div class="pd-field">
                <label class="pd-label">Upload Survey Plan (if available)</label>
                <input type="file" class="pd-input" name="survey_plan" accept="application/pdf,image/jpeg,image/png" />
              </div>

              <div class="pd-field">
                <label class="pd-label">Services Required</label>
                <div class="pd-checkboxes">
                  <label v-for="svc in services" :key="svc.slug" class="pd-checkbox-label">
                    <input type="checkbox" name="services[]" :value="svc.name" />
                    {{ svc.name }}
                  </label>
                </div>
              </div>

              <div class="pd-field">
                <label class="pd-checkbox-label">
                  <input type="checkbox" name="agree_to_structurecity" value="1" />
                  Structurecity will be responsible for your project. All payments will be made to Structurecity and paid to respective personnel on confirmation of deliverables.
                </label>
              </div>

              <div class="pd-field">
                <label class="pd-checkbox-label">
                  <input type="checkbox" name="agree_to_terms" value="1" required checked />
                  Agree to our <router-link to="/terms-and-conditions" target="_blank">terms and conditions of use</router-link>
                </label>
              </div>

              <input type="hidden" name="plan_id" :value="details.id" />
            </form>
          </div>
          <div class="pd-modal-body" v-else>
            <p class="pd-login-notice">Please log in to request a quote.</p>
            <form id="login-form">
              <div class="pd-field">
                <label class="pd-label">Email</label>
                <input type="email" name="email" class="pd-input" />
              </div>
              <div class="pd-field">
                <label class="pd-label">Password</label>
                <input type="password" name="password" class="pd-input" />
              </div>
            </form>
          </div>
          <div class="pd-modal-footer">
            <button class="btn-outline" @click="pdQuoteOpen = false">Cancel</button>
            <button v-if="id" class="btn-primary" :disabled="loading" @click="sendMessage()">
              <span v-if="loading" class="pd-spinner"></span>
              <span v-else>Submit</span>
            </button>
            <button v-else class="btn-primary" :disabled="loading" @click="login()">
              <span v-if="loading" class="pd-spinner"></span>
              <span v-else>Sign In</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Edit Comment Modal -->
    <transition name="pd-fade">
      <div v-if="showEditComment" class="pd-modal-backdrop" @click.self="closeEditPostCommentModal()">
        <div class="pd-modal pd-modal--sm">
          <div class="pd-modal-header">
            <h3 class="pd-modal-title">Edit Comment</h3>
            <button class="pd-modal-close" @click="closeEditPostCommentModal()">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="pd-modal-body">
            <div class="pd-field">
              <label class="pd-label">Comment</label>
              <textarea class="pd-input pd-textarea" rows="5" v-model="edit_comment"></textarea>
            </div>
          </div>
          <div class="pd-modal-footer">
            <button class="btn-outline" @click="closeEditPostCommentModal()">Cancel</button>
            <button class="btn-primary" :disabled="loading" @click="editComment()">
              <span v-if="loading" class="pd-spinner"></span>
              <span v-else>Update</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Reply Comment Modal -->
    <transition name="pd-fade">
      <div v-if="showReplyComment" class="pd-modal-backdrop" @click.self="closeReplyPostCommentModal()">
        <div class="pd-modal pd-modal--sm">
          <div class="pd-modal-header">
            <h3 class="pd-modal-title">Reply to Comment</h3>
            <button class="pd-modal-close" @click="closeReplyPostCommentModal()">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="pd-modal-body">
            <div class="pd-field">
              <textarea class="pd-input pd-textarea" rows="4" v-model="edit_comment" placeholder="Write your reply…"></textarea>
            </div>
          </div>
          <div class="pd-modal-footer">
            <button class="btn-outline" @click="closeReplyPostCommentModal()">Cancel</button>
            <button class="btn-primary" :disabled="loading" @click="replyPostComment()">
              <span v-if="loading" class="pd-spinner"></span>
              <span v-else>Reply</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Delete confirm dialog -->
    <transition name="del-fade">
      <div v-if="confirmDelete" class="del-overlay" @click.self="confirmDelete = false">
        <div class="del-dialog" role="dialog" aria-modal="true">
          <div class="del-dialog__icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
          </div>
          <h3 class="del-dialog__title">Delete Concept?</h3>
          <p class="del-dialog__body">
            This will permanently delete <strong>{{ details.name || details.title }}</strong>. This action cannot be undone.
          </p>
          <div class="del-dialog__actions">
            <button class="del-btn del-btn--cancel" @click="confirmDelete = false" :disabled="deleteLoading">Cancel</button>
            <button class="del-btn del-btn--confirm" @click="deletePlan" :disabled="deleteLoading">
              <span v-if="deleteLoading" class="del-spinner"></span>
              <span v-else>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

  </app-layout>
</template>

<script>
import AppLayout from '@/layouts/AppLayout';
import axios from 'axios';
import moment from 'moment';
import VueEasyLightbox from 'vue-easy-lightbox';
import VueUploadMultipleImage from '@/components/VueUploadMultipleImage';
import { useSEO } from '@/utils/seo.js';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import InfiniteLoading from 'v3-infinite-loading';

export default {
  name: 'PlanDetail',
  components: {
    AppLayout,
    VueEasyLightbox,
    VueUploadMultipleImage,
    QuillEditor,
    InfiniteLoading,
  },

  created() {
    this.fetchDetails();
    this.fectchInterests();
    this.fetchUsers();
    this.fetchAdverts();
  },

  watch: {
    $route: {
      immediate: true,
      handler(to) {
        let subject = to.params.id.substring(0, to.params.id.length - 10);
        document.title = to.meta.title || 'Structurecity | Concept - ' + subject.replace(/-/g, ' ');
      },
    },
  },

  data() {
    return {
      details: {},
      related_plans: [],
      users: [],
      interests: [],
      interest_children: [],
      interest_children2: [],
      images: [],
      loading: false,
      id: localStorage.getItem('user'),
      activeTab: 'overview',
      activeImageIndex: 0,
      visible: false,
      pdAdvertIndex: 0,
      showEditModal: false,
      pdQuoteOpen: false,
      confirmDelete: false,
      showLikersModal: false,
      deleteLoading: false,
      likeLoading: false,
      showEditComment: false,
      showReplyComment: false,
      openMenuId: null,
      plan: {
        user_id: localStorage.getItem('user'),
        title: '',
        id: '',
        project_interest_1: '',
        project_interest_2: '',
        project_interest_3: '',
        description: '',
        height: '',
        land_size: '',
        land_size_unit: '',
        youtube_url: '',
        instagram_url: '',
        images: '',
      },
      index: 0,
      adverts: [],
      page: 1,
      edit_comment: '',
      edit_comment_id: '',
      comment: { description: [] },
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ],
      services: [
        { name: 'Architectural Drawing', slug: 'architectural-drawing' },
        { name: 'Structural Drawing', slug: 'structural-drawing' },
        { name: 'Mechanical Drawing', slug: 'mechanical-drawing' },
        { name: 'Electrical Drawing', slug: 'electrical-drawing' },
        { name: 'Bill of Quantities (BOQ)', slug: 'bill-of-quantities' },
        { name: 'Project construction', slug: 'project-construction' },
      ],
    };
  },

  methods: {
    nameColor(name) {
      const palette = ['#4f46e5','#0891b2','#059669','#d97706','#7c3aed','#db2777','#ea580c','#0284c7'];
      let h = 0;
      for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
      return palette[Math.abs(h) % palette.length];
    },
    initAvatar(name, size = 40) {
      const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
      const bg = this.nameColor(name);
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${size/2}" cy="${size/2}" r="${size/2}" fill="${bg}"/><text x="${size/2}" y="${size/2+5}" font-family="Arial,sans-serif" font-size="${Math.round(size*0.36)}" font-weight="700" fill="#fff" text-anchor="middle">${initials}</text></svg>`;
      return 'data:image/svg+xml;base64,' + btoa(svg);
    },

    fetchDetails() {
      axios(process.env.VUE_APP_API_URL + 'plan/details/' + this.$route.params.id).then(response => {
        if (response.status !== 200) return;
        this.details = response.data;
        const _img = response.data.images && response.data.images[0]
          ? (response.data.images[0].path || (process.env.VUE_APP_IMAGE_URL + 'posts/' + response.data.images[0].image))
          : null;
        useSEO({
          title: response.data.name || response.data.title,
          description: response.data.description,
          image: _img,
          url: window.location.href,
        });
        this.interestChildren(response.data.interests[0] ? response.data.interests[0].interest_id : 0);
        this.interestChildren2(response.data.interests[1] ? response.data.interests[1].interest_id : 0);
        this.fetchRelatedPosts(response.data.id);

        this.plan.id = response.data.id;
        this.plan.user_id = response.data.user_id;
        this.plan.title = response.data.title;
        this.plan.description = response.data.description;
        this.plan.height = response.data.height;
        this.plan.land_size = response.data.land_size;
        this.plan.land_size_unit = response.data.land_size_unit;
        this.plan.project_interest_1 = response.data.interests[0] ? response.data.interests[0].interest_id : '';
        this.plan.project_interest_2 = response.data.interests[1] ? response.data.interests[1].interest_id : '';
        this.plan.project_interest_3 = response.data.interests[2] ? response.data.interests[2].interest_id : '';
        this.plan.youtube_url = response.data.youtube_url || '';
        this.plan.instagram_url = response.data.instagram_url || '';
      }).catch(() => {});
    },

    fetchRelatedPosts(post_id) {
      axios(process.env.VUE_APP_API_URL + 'plan/related/' + post_id).then(response => {
        if (response.status !== 200) return;
        this.related_plans = response.data.data;
        this.page = this.page + 1;
      }).catch(() => {});
    },

    handleLoadMore($state) {
      axios(process.env.VUE_APP_API_URL + 'plan/related/' + this.details.id + '?page=' + this.page).then(res => {
        res.data.data.forEach(v => this.related_plans.push(v));
        $state.loaded();
        this.page = this.page + 1;
        if (res.data.data.length <= 0) $state.complete();
      });
    },

    fetchUsers() {
      axios(process.env.VUE_APP_API_URL + 'users').then(response => {
        if (response.status !== 200) return;
        this.users = response.data;
      }).catch(() => {});
    },

    fectchInterests() {
      axios(process.env.VUE_APP_API_URL + 'project-interest').then(response => {
        if (response.status !== 200) return;
        this.interests = response.data;
      }).catch(() => {});
    },

    fectchInterestChildren(e) {
      this.interest_children = [];
      this.interest_children2 = [];
      axios(process.env.VUE_APP_API_URL + 'interest/' + e.target.value).then(response => {
        if (response.status !== 200) return;
        this.interest_children = response.data;
      }).catch(() => {});
    },

    fectchInterestChildren2(e) {
      this.interest_children2 = [];
      axios(process.env.VUE_APP_API_URL + 'interest/' + e.target.value).then(response => {
        if (response.status !== 200) return;
        this.interest_children2 = response.data;
      }).catch(() => {});
    },

    interestChildren(id) {
      axios(process.env.VUE_APP_API_URL + 'interest/' + id).then(response => {
        if (response.status !== 200) return;
        this.interest_children = response.data;
      }).catch(() => {});
    },

    interestChildren2(id) {
      axios(process.env.VUE_APP_API_URL + 'interest/' + id).then(response => {
        if (response.status !== 200) return;
        this.interest_children2 = response.data;
      }).catch(() => {});
    },

    fetchAdverts() {
      axios(process.env.VUE_APP_API_URL + 'adverts').then(response => {
        if (response.status !== 200) return;
        this.adverts = response.data;
      }).catch(() => {});
    },

    openUpdateProject() {
      this.showEditModal = true;
    },

    updateProject() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'plan/update', this.plan).then(response => {
        if (response.status === 200) {
          this.$toastr.s('Plan updated successfully');
          window.Fire.emit('delete-team-action');
          this.showEditModal = false;
          this.images = [];
          this.loading = false;
        } else {
          this.$toastr.e(response.data.message);
          this.loading = false;
        }
      }).catch(error => {
        this.$toastr.e(error.response.data.message);
        this.loading = false;
      });
    },

    cancelForm() {
      if (confirm('Are you sure you want to discard changes?')) {
        this.showEditModal = false;
        this.images = [];
      }
    },

    likePlan() {
      if (this.alreadyLiked || this.likeLoading) return;
      this.likeLoading = true;
      axios.post(process.env.VUE_APP_API_URL + 'plan/like', {
        plan_id: this.details.id,
        user_id: this.id,
      })
        .then(res => {
          if (res.status === 200) {
            if (!this.details.likes) this.details.likes = [];
            this.details.likes.push({ user_id: this.id });
            this.$toastr.s('Liked!');
          } else {
            this.$toastr.e(res.message || 'Could not like concept.');
          }
          this.likeLoading = false;
        })
        .catch(() => {
          this.$toastr.e('Could not like concept.');
          this.likeLoading = false;
        });
    },

    deletePlan() {
      this.deleteLoading = true;
      axios.delete(process.env.VUE_APP_API_URL + 'plan/delete/' + this.details.id)
        .then(res => {
          if (res.status === 200 || res.status === 204) {
            this.$toastr.s('Concept deleted successfully.');
            this.$router.push({ name: 'Home' });
          } else {
            this.$toastr.e(res.message || 'Could not delete concept.');
            this.confirmDelete = false;
          }
          this.deleteLoading = false;
        })
        .catch(() => {
          this.$toastr.e('Could not delete concept.');
          this.deleteLoading = false;
        });
    },

    sendMessage() {
      this.loading = true;
      var form = document.getElementById('message-form');
      var _data = new FormData(form);
      _data.append('user_id', localStorage.getItem('user'));
      axios.post(process.env.VUE_APP_API_URL + 'plan/request-quote', _data).then(response => {
        if (response.status === 200) {
          this.$toastr.s('Quote request sent!');
          this.loading = false;
          form.reset();
          this.pdQuoteOpen = false;
        } else {
          this.$toastr.e(response.data.message);
          this.loading = false;
        }
      }).catch(error => {
        this.$toastr.e(error.response.data.message);
        this.loading = false;
      });
    },

    login() {
      this.loading = true;
      var form = document.getElementById('login-form');
      var _data = new FormData(form);
      axios.post(process.env.VUE_APP_API_URL + 'auth/login', _data).then(response => {
        if (response.status === 200) {
          this.pdQuoteOpen = false;
          localStorage.setItem('user', response.data.data.id);
          localStorage.setItem('slug', response.data.data.slug);
          this.$router.go();
          this.loading = false;
        } else {
          this.$toastr.e(response.data.message);
          this.loading = false;
        }
      }).catch(error => {
        this.$toastr.e(error.response.data.message);
        this.loading = false;
      });
    },

    deleteProjectImage(id) {
      axios(process.env.VUE_APP_API_URL + 'plan/delete-plan-image/' + id).then(response => {
        if (response.status === 200) {
          this.$toastr.s('Image removed');
          window.Fire.emit('delete-team-action');
        } else {
          this.$toastr.e(response.data.message);
        }
      }).catch(error => {
        this.$toastr.e(error.response.data.message);
      });
    },

    setCoverImage(imageId) {
      axios.post(process.env.VUE_APP_API_URL + 'plan/set-cover-image', {
        plan_id: this.details.id,
        image_id: imageId,
      }, { skipToast: true }).then(res => {
        if (res.status === 200) {
          this.details.images.forEach(img => { img.is_cover = img.id === imageId ? 1 : 0; });
        }
      }).catch(() => {});
    },

    uploadImageSuccess(formData, index, fileList) {
      this.plan.images = fileList;
    },

    beforeRemove(index, done) {
      if (confirm('Remove image?')) done();
    },

    editImage() {},
    dataChange() {},

    formatDate(date) {
      return moment(date).fromNow();
    },

    formatDate2(date) {
      return moment(date).format('DD MMMM, YYYY');
    },

    showImg(index) {
      this.activeImageIndex = index;
      this.visible = true;
    },

    handleHide() {
      this.visible = false;
    },

    toggleMenu(id) {
      this.openMenuId = this.openMenuId === id ? null : id;
    },

    submitComment(event, id) {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'plan/comment', {
        comment: event.target.value, plan_id: id, user_id: localStorage.getItem('user'),
      }).then(response => {
        if (response.status === 200) {
          this.comment.description = [];
          window.Fire.emit('banner-action');
          this.loading = false;
        } else {
          this.$toastr.e(response.data.message);
          this.loading = false;
        }
      }).catch(error => {
        this.$toastr.e(error.response.data.message);
        this.loading = false;
      });
    },

    sendSubmitComment(id) {
      const el = document.getElementById('submitCommentButton' + id);
      if (!el || !el.value.trim()) return;
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'plan/comment', {
        comment: el.value, plan_id: id, user_id: localStorage.getItem('user'),
      }).then(response => {
        if (response.status === 200) {
          this.comment.description = [];
          el.value = '';
          window.Fire.emit('banner-action');
          this.loading = false;
        } else {
          this.$toastr.e(response.data.message);
          this.loading = false;
        }
      }).catch(error => {
        this.$toastr.e(error.response.data.message);
        this.loading = false;
      });
    },

    editPostComment(comment_id, comment) {
      this.edit_comment = comment;
      this.edit_comment_id = comment_id;
      this.openMenuId = null;
      this.showEditComment = true;
    },

    closeEditPostCommentModal() {
      this.edit_comment = '';
      this.edit_comment_id = '';
      this.showEditComment = false;
    },

    editComment() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'plan/comment/edit', {
        plan_id: this.details.id, comment: this.edit_comment, comment_id: this.edit_comment_id, user_id: localStorage.getItem('user'),
      }).then(response => {
        if (response.status === 200) {
          this.closeEditPostCommentModal();
          window.Fire.emit('banner-action');
          this.loading = false;
        } else {
          this.$toastr.e(response.data.message);
          this.loading = false;
        }
      }).catch(error => {
        this.$toastr.e(error.response.data.message);
        this.loading = false;
      });
    },

    deleteComment(comment_id) {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'plan/comment/delete', {
        plan_id: this.details.id, comment_id, user_id: localStorage.getItem('user'),
      }).then(response => {
        if (response.status === 200) {
          window.Fire.emit('banner-action');
          this.loading = false;
        } else {
          this.$toastr.e(response.data.message);
          this.loading = false;
        }
      }).catch(error => {
        this.$toastr.e(error.response.data.message);
        this.loading = false;
      });
    },

    likePostComment(comment_id) {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'plan/comment/like', {
        comment_id, user_id: localStorage.getItem('user'),
      }).then(response => {
        if (response.status === 200) {
          window.Fire.emit('banner-action');
          this.loading = false;
        } else {
          this.loading = false;
        }
      }).catch(() => { this.loading = false; });
    },

    unlikePostComment(comment_id) {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'plan/comment/unlike', {
        comment_id, user_id: localStorage.getItem('user'),
      }).then(response => {
        if (response.status === 200) {
          window.Fire.emit('banner-action');
          this.loading = false;
        } else {
          this.loading = false;
        }
      }).catch(() => { this.loading = false; });
    },

    checkIfUser(source) {
      if (!source || !source.length) return false;
      return source.some(s => s.user_id == this.id);
    },

    replyPostCommentModal(comment_id) {
      this.edit_comment_id = comment_id;
      this.edit_comment = '';
      this.showReplyComment = true;
    },

    closeReplyPostCommentModal() {
      this.edit_comment_id = '';
      this.edit_comment = '';
      this.showReplyComment = false;
    },

    replyPostComment() {
      this.loading = true;
      axios.post(process.env.VUE_APP_API_URL + 'plan/comment/reply', {
        plan_id: this.details.id, comment: this.edit_comment, parent_id: this.edit_comment_id, user_id: localStorage.getItem('user'),
      }).then(response => {
        if (response.status === 200) {
          this.closeReplyPostCommentModal();
          window.Fire.emit('banner-action');
          this.loading = false;
        } else {
          this.$toastr.e(response.data.message);
          this.loading = false;
        }
      }).catch(error => {
        this.$toastr.e(error.response.data.message);
        this.loading = false;
      });
    },

    logout() {
      localStorage.clear();
      this.$router.go();
    },
  },

  mounted() {
    window.Fire.on('banner-action', this.fetchDetails);
    window.Fire.on('delete-team-action', this.fetchDetails);
  },

  beforeUnmount() {
    window.Fire.off('banner-action', this.fetchDetails);
    window.Fire.off('delete-team-action', this.fetchDetails);
  },

  computed: {
    image_url() {
      return process.env.VUE_APP_IMAGE_URL;
    },
    alreadyLiked() {
      if (!this.id || !this.details.likes) return false;
      return this.details.likes.some(l => String(l.user_id) === String(this.id));
    },
    youtubeVideoId() {
      const url = this.details && this.details.youtube_url;
      if (!url) return '';
      const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
      return m ? m[1] : '';
    },
    instagramShortcode() {
      const url = this.details && this.details.instagram_url;
      if (!url) return '';
      const m = url.match(/instagram\.com\/(?:p|reel)\/([A-Za-z0-9_-]+)/);
      return m ? m[1] : '';
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style>
:deep(.ql-editor) { min-height: 140px; }
.sc-video-wrap { position: relative; width: 100%; padding-top: 56.25%; border-radius: 8px; overflow: hidden; background: #000; }
.sc-video-wrap--instagram { padding-top: 130%; max-width: 540px; background: #fafafa; }
.sc-video-frame { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; }
</style>

<style scoped>
/* ── Layout ── */
.pd-page { padding: 24px 0 64px; }
.detail-layout { display: grid; grid-template-columns: 1fr 320px; gap: 24px; align-items: start; }
@media (max-width: 900px) { .detail-layout { grid-template-columns: 1fr; } }

/* ── sc-card reuse (scoped copies matching global) ── */
.sc-card { background: var(--surface, #fff); border: 1px solid var(--border-light, #e5e7eb); border-radius: var(--radius-lg, 12px); box-shadow: var(--shadow, 0 1px 6px rgba(0,0,0,.06)); margin-bottom: 16px; overflow: hidden; }
.sc-card-header { padding: 14px 20px; font-size: .875rem; font-weight: 600; color: var(--text, #111827); border-bottom: 1px solid var(--border-light, #e5e7eb); }
.sc-card-body { padding: 20px; }

/* ── Title bar ── */
.title-bar { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.title-bar-left { flex: 1; }
.detail-title { font-size: 1.5rem; font-weight: 700; color: var(--text, #111827); margin: 0 0 8px; }
.detail-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.meta-date { font-size: .8rem; color: var(--text-muted, #6b7280); }
.meta-tag { font-size: .78rem; font-weight: 600; color: var(--primary, #1e3a5f); text-decoration: none; background: #eff6ff; border-radius: 4px; padding: 2px 8px; }
.meta-tag:hover { text-decoration: underline; }
.title-bar-right { display: flex; gap: 8px; flex-shrink: 0; }

.btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px; border-radius: 8px; font-size: .875rem; font-weight: 600;
  background: var(--primary, #1e3a5f); color: #fff; border: none; cursor: pointer;
  transition: filter .15s;
}
.btn-primary:hover:not(:disabled) { filter: brightness(1.12); }
.btn-primary:disabled { opacity: .6; cursor: not-allowed; }

.btn-outline {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 18px; border-radius: 8px; font-size: .875rem; font-weight: 600;
  background: transparent; border: 1.5px solid var(--border-light, #e5e7eb);
  color: var(--text, #111827); cursor: pointer; transition: background .15s;
}
.btn-outline:hover { background: #f3f4f6; }

/* ── Gallery ── */
.gallery-hero {
  position: relative; width: 100%; height: 420px; border-radius: 12px;
  overflow: hidden; cursor: pointer; margin-bottom: 8px; background: #f3f4f6;
}
.gallery-hero img { width: 100%; height: 100%; object-fit: cover; display: block; }
.gallery-overlay-hint {
  position: absolute; bottom: 10px; right: 12px;
  background: rgba(0,0,0,.45); color: #fff; font-size: .75rem;
  padding: 4px 10px; border-radius: 20px; pointer-events: none;
}
.gallery-thumbs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
.gallery-thumb { width: 72px; height: 54px; border-radius: 6px; overflow: hidden; cursor: pointer; border: 2px solid transparent; transition: border-color .15s; }
.gallery-thumb.active { border-color: var(--primary, #1e3a5f); }
.gallery-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.gallery-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; border-radius: 12px; background: #f9fafb; border: 1px dashed var(--border-light, #d1d5db); color: var(--text-muted, #9ca3af); gap: 8px; margin-bottom: 16px; }

/* ── Tabs ── */
.filter-tabs { display: flex; gap: 0; border-bottom: none; }
.filter-tab {
  padding: 12px 20px; font-size: .875rem; font-weight: 500;
  border: none; background: none; cursor: pointer; color: var(--text-muted, #6b7280);
  border-bottom: 2px solid transparent; transition: color .15s, border-color .15s;
}
.filter-tab.active { color: var(--primary, #1e3a5f); border-bottom-color: var(--primary, #1e3a5f); font-weight: 600; }
.filter-tab:hover { color: var(--primary, #1e3a5f); }

/* ── Details grid ── */
.details-grid { display: flex; flex-direction: column; gap: 0; }
.detail-row { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid var(--border-light, #f3f4f6); }
.detail-row:last-child { border-bottom: none; }
.detail-label { font-size: .875rem; color: var(--text-muted, #6b7280); font-weight: 500; }
.detail-value { font-size: .875rem; color: var(--text, #111827); font-weight: 500; text-align: right; max-width: 60%; }

/* ── Status badges ── */
.status-badge { font-size: .75rem; font-weight: 600; padding: 3px 9px; border-radius: 12px; display: inline-block; }
.badge-success { background: #d1fae5; color: #065f46; }
.badge-warning { background: #fef3c7; color: #92400e; }

/* ── Prose ── */
.prose-content { font-size: .9rem; line-height: 1.7; color: var(--text, #374151); }

/* ── Comments ── */
.pd-comment {
  display: flex; gap: 12px; padding: 16px 20px;
  border-bottom: 1px solid var(--border-light, #f3f4f6);
}
.pd-comment:last-child { border-bottom: none; }
.pd-comment--reply { padding: 12px 0 0; }
.pd-comment-avatar { flex-shrink: 0; }
.pd-avatar-img { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; display: block; }
.pd-avatar-img--sm { width: 30px; height: 30px; }
.pd-comment-body { flex: 1; min-width: 0; }
.pd-comment-header { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 4px; position: relative; }
.pd-comment-name { font-weight: 600; font-size: .875rem; color: var(--text, #111827); text-decoration: none; }
.pd-comment-name:hover { text-decoration: underline; }
.pd-comment-date { font-size: .75rem; color: var(--text-muted, #9ca3af); }
.pd-comment-text { font-size: .875rem; color: var(--text, #374151); margin-bottom: 6px; word-break: break-word; }
.pd-comment-actions { display: flex; align-items: center; gap: 12px; }
.pd-action-btn { background: none; border: none; font-size: .78rem; color: var(--text-muted, #6b7280); cursor: pointer; padding: 0; }
.pd-action-btn:hover { color: var(--primary, #1e3a5f); }
.pd-action-count { display: flex; align-items: center; gap: 4px; font-size: .78rem; color: var(--text-muted, #6b7280); }

.pd-comment-menu { margin-left: auto; position: relative; }
.pd-menu-btn { background: none; border: none; font-size: 1.1rem; color: var(--text-muted, #9ca3af); cursor: pointer; padding: 0 4px; line-height: 1; }
.pd-dropdown {
  position: absolute; right: 0; top: 100%; z-index: 10;
  background: #fff; border: 1px solid var(--border-light, #e5e7eb);
  border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,.1);
  min-width: 100px; overflow: hidden;
}
.pd-dropdown button { display: block; width: 100%; padding: 9px 14px; text-align: left; background: none; border: none; font-size: .875rem; cursor: pointer; color: var(--text, #111827); }
.pd-dropdown button:hover { background: #f3f4f6; }

.pd-replies { margin-top: 8px; padding-left: 16px; border-left: 2px solid var(--border-light, #e5e7eb); }

.pd-no-comments { padding: 24px 20px; color: var(--text-muted, #9ca3af); font-size: .9rem; text-align: center; }

.pd-comment-input-wrap { display: flex; align-items: flex-end; gap: 10px; border-top: 1px solid var(--border-light, #e5e7eb); }
.pd-comment-input {
  flex: 1; border: 1.5px solid var(--border-light, #e5e7eb);
  border-radius: 8px; padding: 10px 14px; font-size: .875rem;
  font-family: inherit; resize: none; outline: none;
  transition: border-color .15s;
}
.pd-comment-input:focus { border-color: var(--primary, #1e3a5f); }
.pd-comment-send {
  flex-shrink: 0; width: 38px; height: 38px; border-radius: 8px;
  background: var(--primary, #1e3a5f); color: #fff; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: filter .15s;
}
.pd-comment-send:hover:not(:disabled) { filter: brightness(1.15); }
.pd-comment-send:disabled { opacity: .5; cursor: not-allowed; }

/* ── Related plans ── */
.pd-related-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 16px; margin-bottom: 16px; }
.pd-related-card { padding: 0; }
.pd-related-img { width: 100%; height: 180px; object-fit: cover; display: block; }
.pd-related-img-placeholder { width: 100%; height: 180px; display: flex; align-items: center; justify-content: center; background: #f3f4f6; color: #9ca3af; }
.pd-related-body { padding: 12px 16px 16px; }
.pd-related-title { display: block; font-size: .9rem; font-weight: 600; color: var(--text, #111827); text-decoration: none; margin: 6px 0 8px; }
.pd-related-title:hover { color: var(--primary, #1e3a5f); }
.pd-related-author { display: flex; align-items: center; justify-content: space-between; gap: 6px; font-size: .78rem; color: var(--text-muted, #6b7280); }
.pd-related-author a { display: flex; align-items: center; gap: 6px; text-decoration: none; color: inherit; }
.pd-related-date { font-size: .72rem; }

/* ── Sidebar ── */
.pd-author-body { display: flex; flex-direction: column; }
.pd-author-link { display: flex; gap: 14px; text-decoration: none; color: inherit; }
.pd-author-avatar { width: 52px; height: 52px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.pd-author-info { display: flex; flex-direction: column; gap: 2px; }
.pd-author-name { font-size: .925rem; font-weight: 700; color: var(--text, #111827); }
.pd-author-sub { font-size: .8rem; color: var(--text-muted, #6b7280); }
.pd-author-loc { font-size: .78rem; color: var(--text-muted, #9ca3af); }

.pd-quote-body { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 12px; }
.pd-quote-body p { margin: 0; font-size: .875rem; color: var(--text-muted, #6b7280); }
.pd-quote-btn { width: 100%; justify-content: center; }

.pd-advert-card { overflow: hidden; }
.pd-advert-inner { position: relative; }
.pd-advert-img { width: 100%; display: block; }
.pd-advert-dots { display: flex; justify-content: center; gap: 6px; padding: 10px; }
.pd-advert-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--border-light, #d1d5db); border: none; cursor: pointer; padding: 0; transition: background .15s; }
.pd-advert-dot.active { background: var(--primary, #1e3a5f); }

/* ── Modals ── */
.pd-modal-backdrop {
  position: fixed; inset: 0; z-index: 1050;
  background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.pd-modal {
  background: #fff; border-radius: 14px;
  box-shadow: 0 8px 40px rgba(0,0,0,.18);
  width: 100%; max-width: 620px; max-height: 90vh;
  display: flex; flex-direction: column; overflow: hidden;
}
.pd-modal--sm { max-width: 480px; }
.pd-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 18px 24px 14px; border-bottom: 1px solid var(--border-light, #e5e7eb); flex-shrink: 0; }
.pd-modal-title { font-size: 1.1rem; font-weight: 700; color: var(--text, #111827); margin: 0; }
.pd-modal-close { background: none; border: none; cursor: pointer; color: var(--text-muted, #6b7280); display: flex; align-items: center; padding: 4px; border-radius: 6px; }
.pd-modal-close:hover { background: #f3f4f6; color: var(--text, #111827); }
.pd-modal-body { padding: 20px 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; flex: 1; }
.pd-modal-footer { padding: 14px 24px; border-top: 1px solid var(--border-light, #e5e7eb); display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0; }

.pd-field { display: flex; flex-direction: column; gap: 6px; }
.pd-label { font-size: .875rem; font-weight: 500; color: var(--text, #374151); }
.req { color: #ef4444; }
.pd-input {
  width: 100%; padding: 10px 14px;
  border: 1.5px solid var(--border-light, #e5e7eb);
  border-radius: 8px; font-size: .9rem;
  background: #fff; color: var(--text, #111827);
  outline: none; transition: border-color .18s;
  font-family: inherit; box-sizing: border-box;
}
.pd-input:focus { border-color: var(--primary, #1e3a5f); }
.pd-textarea { resize: vertical; min-height: 80px; }
.pd-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.pd-category-row { display: flex; flex-wrap: wrap; gap: 10px; }
.pd-category-row .pd-input { flex: 1 1 140px; min-width: 0; }

.pd-img-grid { display: flex; flex-wrap: wrap; gap: 10px; }
.pd-img-thumb { position: relative; width: 72px; height: 72px; }
.pd-img-thumb img { width: 100%; height: 100%; object-fit: cover; border-radius: 6px; }
.pd-img-thumb--cover img { outline: 2px solid #f59e0b; outline-offset: 2px; }
.pd-img-remove {
  position: absolute; top: -6px; right: -6px; width: 20px; height: 20px;
  border-radius: 50%; background: #ef4444; color: #fff;
  border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0;
}
.pd-img-cover {
  position: absolute; bottom: -6px; left: -6px; width: 22px; height: 22px;
  border-radius: 50%; background: #fff; border: 1.5px solid #d1d5db; color: #9ca3af;
  cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0;
  transition: border-color .15s, color .15s;
}
.pd-img-cover:hover { border-color: #f59e0b; color: #f59e0b; }
.pd-img-cover--active { border-color: #f59e0b; color: #f59e0b; background: #fffbeb; }

.pd-checkboxes { display: flex; flex-direction: column; gap: 8px; }
.pd-checkbox-label { display: flex; align-items: flex-start; gap: 8px; font-size: .875rem; color: var(--text, #374151); cursor: pointer; line-height: 1.4; }
.pd-checkbox-label input { flex-shrink: 0; margin-top: 2px; }

.pd-login-notice { font-size: .9rem; color: var(--text-muted, #6b7280); margin: 0 0 4px; }

/* ── Spinner ── */
.pd-spinner {
  width: 15px; height: 15px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.35); border-top-color: #fff;
  animation: pd-spin .7s linear infinite; display: inline-block;
}
@keyframes pd-spin { to { transform: rotate(360deg); } }

/* ── Transitions ── */
.pd-fade-enter-active, .pd-fade-leave-active { transition: opacity .2s; }
.pd-fade-enter-from, .pd-fade-leave-to { opacity: 0; }

.infinite-wrapper { margin: 8px 0 24px; }

.pd-tabs-bar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
.pd-tabs-bar .filter-tabs { flex: 1; border-bottom: none; }
.pd-tab-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 4px; margin-left: 5px;
  border-radius: 9px; background: var(--primary, #1e3a5f); color: #fff;
  font-size: .7rem; font-weight: 700; line-height: 1;
}
.pd-actions-bar { display: flex; align-items: center; gap: .5rem; padding-right: .5rem; flex-shrink: 0; }
.pd-like-btn {
  display: inline-flex; align-items: center; gap: .375rem;
  padding: .4rem .85rem; border-radius: var(--radius-full, 999px);
  border: 1.5px solid var(--border-light, #e5e7eb);
  background: none; cursor: pointer; font-size: .875rem; font-weight: 600;
  color: var(--text-muted, #6b7280);
  transition: background .15s, color .15s, border-color .15s;
}
.pd-like-btn:hover:not(:disabled) { background: #fee2e2; color: #dc2626; border-color: #fca5a5; }
.pd-like-btn--liked { background: #fee2e2; color: #dc2626; border-color: #fca5a5; cursor: default; }
.pd-like-btn:disabled:not(.pd-like-btn--liked) { opacity: .6; cursor: not-allowed; }
.pd-likes-wrap { display: flex; align-items: center; gap: .5rem; }
.pd-likers-btn { display: flex; align-items: center; gap: .375rem; background: none; border: none; cursor: pointer; padding: .25rem; border-radius: .5rem; transition: background .12s; }
.pd-likers-btn:hover { background: var(--surface-3, #f1f5f9); }
.pd-likers-stack { display: flex; }
.pd-likers-avatar { width: 24px; height: 24px; border-radius: 50%; object-fit: cover; border: 2px solid #fff; margin-left: -6px; }
.pd-likers-stack .pd-likers-avatar:first-child { margin-left: 0; }
.pd-likers-more { font-size: .75rem; font-weight: 600; color: var(--text-muted, #6b7280); }
.likers-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); z-index: 800; display: flex; align-items: center; justify-content: center; padding: 1rem; }
.likers-modal { background: #fff; border-radius: 1rem; width: 100%; max-width: 380px; max-height: 70vh; display: flex; flex-direction: column; box-shadow: 0 20px 48px rgba(0,0,0,.18); }
.likers-modal__header { display: flex; align-items: center; justify-content: space-between; padding: .875rem 1rem; border-bottom: 1px solid var(--border-light, #f3f4f6); font-weight: 700; font-size: .9375rem; }
.likers-modal__close { background: none; border: none; cursor: pointer; color: var(--text-muted, #6b7280); display: flex; padding: .25rem; border-radius: .375rem; transition: background .12s; }
.likers-modal__close:hover { background: var(--surface-2, #f8fafc); }
.likers-modal__body { overflow-y: auto; padding: .5rem 0; }
.likers-item { display: flex; align-items: center; gap: .75rem; padding: .625rem 1rem; text-decoration: none; transition: background .12s; }
.likers-item:hover { background: var(--surface-2, #f8fafc); }
.likers-item__avatar { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.likers-item__info { display: flex; flex-direction: column; min-width: 0; }
.likers-item__name { font-size: .875rem; font-weight: 600; color: var(--text, #111827); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.likers-item__role { font-size: .75rem; color: var(--text-muted, #6b7280); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.btn-danger {
  display: inline-flex; align-items: center; gap: .375rem;
  padding: .5rem 1rem; border-radius: .5rem; border: none; cursor: pointer;
  background: #fee2e2; color: #dc2626;
  font-size: .875rem; font-weight: 600;
  transition: background .15s;
}
.btn-danger:hover { background: #fecaca; }

.del-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.del-dialog {
  background: var(--surface, #fff); border-radius: 1rem;
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
  padding: 2rem 1.75rem 1.5rem;
  width: 100%; max-width: 380px; text-align: center;
  animation: del-pop .18s ease;
}
@keyframes del-pop { from { opacity:0; transform:scale(.93); } to { opacity:1; transform:scale(1); } }
.del-dialog__icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 60px; height: 60px; border-radius: 50%;
  background: #fee2e2; color: #dc2626; margin-bottom: 1rem;
}
.del-dialog__title { font-size: 1.125rem; font-weight: 700; color: var(--text,#111); margin: 0 0 .5rem; }
.del-dialog__body { font-size: .9rem; color: var(--text-muted,#6b7280); line-height: 1.5; margin: 0 0 1.5rem; }
.del-dialog__body strong { color: var(--text,#111); }
.del-dialog__actions { display: flex; gap: .75rem; justify-content: center; }
.del-btn {
  flex: 1; padding: .6rem 1rem; border-radius: .625rem;
  font-size: .9rem; font-weight: 600; cursor: pointer; border: none;
  transition: background .15s, opacity .15s;
  display: flex; align-items: center; justify-content: center; gap: .5rem;
}
.del-btn:disabled { opacity: .6; cursor: not-allowed; }
.del-btn--cancel { background: var(--surface-2,#f3f4f6); color: var(--text,#111); }
.del-btn--cancel:hover:not(:disabled) { background: var(--border-light,#e5e7eb); }
.del-btn--confirm { background: #dc2626; color: #fff; }
.del-btn--confirm:hover:not(:disabled) { background: #b91c1c; }
.del-spinner {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.4); border-top-color: #fff;
  animation: pd-spin .6s linear infinite; display: inline-block;
}
.del-fade-enter-active, .del-fade-leave-active { transition: opacity .18s; }
.del-fade-enter-from, .del-fade-leave-to { opacity: 0; }
</style>
